/**
 * MODULE: _shared/frontend-shell/src/components/assistant/AssistantContextSessionDelta.tsx
 * PURPOSE: Since-last-session metric changes with optional CRM deep links [2.2.1225].
 * RECENT: 2026-05-26 [2.2.1257] — risk-shift amber styling for momentum + insight escalation deltas.
 * PUBLIC_API: AssistantContextSessionDelta
 * AGENT_SAFETY: SAFE
 */

import { Link } from 'react-router-dom';
import type { AssistantSessionDeltaChange } from '@shared/lib/frontend/api/assistantContextApi';
import { entityPath } from './assistantContextEntityLinks';

export type AssistantContextSessionDeltaProps = {
  changes: AssistantSessionDeltaChange[];
  /** When true, omit section heading (e.g. proactive welcome already has one). */
  inline?: boolean;
  maxItems?: number;
};

export function AssistantContextSessionDelta({
  changes,
  inline = false,
  maxItems = 4,
}: AssistantContextSessionDeltaProps) {
  const visible = changes.filter((c) => (c.description || '').trim()).slice(0, maxItems);
  if (visible.length === 0) return null;

  return (
    <section
      aria-label="Changes since last session"
      className={
        inline
          ? 'w-full max-w-lg text-left'
          : 'rounded-lg border border-sky-200 bg-sky-50/90 p-3 dark:border-sky-900/50 dark:bg-sky-950/30'
      }
      data-testid="assistant-context-session-delta"
    >
      {!inline ? (
        <p className="text-[11px] font-semibold uppercase tracking-wide text-sky-800 dark:text-sky-200">
          Since your last session
        </p>
      ) : null}
      <ul
        className={`space-y-1 text-xs text-sky-950 dark:text-sky-100 ${inline ? '' : 'mt-1'}`}
      >
        {visible.map((change, idx) => {
          const path =
            change.entity_type && change.entity_id
              ? entityPath({
                  entity_type: change.entity_type,
                  entity_id: change.entity_id,
                })
              : null;
          const isPositive =
            change.type === 'engagement_gain' ||
            change.type === 'at_risk_recovered' ||
            change.type === 'stalling_recovered' ||
            change.type === 'deal_momentum_up' ||
            change.type === 'new_insight' ||
            change.type === 'learned_preferences' ||
            change.type === 'activity_signal';
          const isDismissedPref = change.type === 'preferences_dismissed';
          const isRiskShift =
            change.type === 'deal_momentum_down' ||
            change.type === 'insight_escalated' ||
            change.type === 'engagement_drop' ||
            change.type === 'at_risk_deals' ||
            change.type === 'stalling_deals';
          const positiveClass = isPositive
            ? inline
              ? 'rounded-md border border-emerald-200/80 bg-emerald-50/90 px-3 py-2 text-emerald-950 dark:border-emerald-900/50 dark:bg-emerald-950/30 dark:text-emerald-100'
              : 'rounded-md border border-emerald-200/60 bg-emerald-50/70 px-2 py-1 dark:border-emerald-900/40 dark:bg-emerald-950/25'
            : isDismissedPref
              ? inline
                ? 'rounded-md border border-violet-200/80 bg-violet-50/90 px-3 py-2 text-violet-950 dark:border-violet-900/50 dark:bg-violet-950/30 dark:text-violet-100'
                : 'rounded-md border border-violet-200/60 bg-violet-50/70 px-2 py-1 dark:border-violet-900/40 dark:bg-violet-950/25'
              : isRiskShift
                ? inline
                  ? 'rounded-md border border-amber-200/80 bg-amber-50/90 px-3 py-2 text-amber-950 dark:border-amber-900/50 dark:bg-amber-950/30 dark:text-amber-100'
                  : 'rounded-md border border-amber-200/60 bg-amber-50/70 px-2 py-1 dark:border-amber-900/40 dark:bg-amber-950/25'
                : inline
                  ? 'rounded-md bg-sky-50/80 px-3 py-2 dark:bg-sky-950/30'
                  : undefined;
          const inner = <span>{change.description}</span>;
          return (
            <li
              key={`${change.type ?? 'change'}-${idx}`}
              data-testid="assistant-context-session-delta-item"
              data-delta-tone={
                isPositive
                  ? 'positive'
                  : isDismissedPref
                    ? 'dismissed'
                    : isRiskShift
                      ? 'risk'
                      : 'neutral'
              }
              className={positiveClass}
            >
              {path ? (
                <Link
                  to={path}
                  className={`block transition hover:underline ${
                    isPositive
                      ? 'hover:text-emerald-800 dark:hover:text-emerald-200'
                      : isDismissedPref
                        ? 'hover:text-violet-800 dark:hover:text-violet-200'
                        : isRiskShift
                          ? 'hover:text-amber-800 dark:hover:text-amber-200'
                          : 'hover:text-sky-700 dark:hover:text-sky-200'
                  }`}
                  data-testid="assistant-context-session-delta-link"
                >
                  {inner}
                </Link>
              ) : (
                inner
              )}
            </li>
          );
        })}
      </ul>
    </section>
  );
}
